/**
 * Bac à sable Python — programmes d'exemple, du plus simple (4e-3e : condition,
 * boucle bornée) au lycée (fonctions, listes, seuil, dichotomie, Euclide).
 * Tous tournent sur l'interpréteur sûr du moteur (arithmétique exacte).
 */

export interface Example {
  id: string;
  label: string;
  /** rang de niveau à partir duquel le proposer */
  minRank: number;
  code: string;
}

export const EXAMPLES: readonly Example[] = [
  {
    id: 'if',
    label: 'Si… sinon',
    minRank: 3,
    code: `note = 13
if note >= 10:
    resultat = "admis"
else:
    resultat = "refusé"
print(resultat)`,
  },
  {
    id: 'for',
    label: 'Boucle for',
    minRank: 3,
    code: `s = 0
for i in range(1, 6):
    s = s + i
print(s)`,
  },
  {
    id: 'function',
    label: 'Fonction',
    minRank: 5,
    code: `def f(x):
    return x**2 - 3*x + 1

for x in range(-1, 4):
    print(x, f(x))`,
  },
  {
    id: 'list',
    label: 'Liste',
    minRank: 5,
    code: `notes = [12, 8, 15, 9, 17]
admis = []
for n in notes:
    if n >= 10:
        admis.append(n)
print(admis, len(admis))`,
  },
  {
    id: 'while',
    label: 'Seuil (while)',
    minRank: 6,
    code: `u = 1000
n = 0
while u < 2000:
    u = u * 1.05
    n = n + 1
print(n)`,
  },
  {
    id: 'dicho',
    label: 'Dichotomie',
    minRank: 6,
    code: `a = 1
b = 2
while b - a > 0.01:
    m = (a + b) / 2
    if m**2 < 2:
        a = m
    else:
        b = m
print(a, b)`,
  },
  {
    id: 'euclid',
    label: 'Euclide (PGCD)',
    minRank: 7,
    code: `def pgcd(a, b):
    while b != 0:
        a, b = b, a % b
    return a

print(pgcd(84, 36))`,
  },
];

export const examplesFor = (rank: number) => EXAMPLES.filter((e) => e.minRank <= rank);

/** touches d'aide à la saisie (téléphone) : ce qu'on insère au curseur */
export const KEYS: ReadonlyArray<{ label: string; text: string; aria: string }> = [
  { label: '⇥', text: '    ', aria: 'Indenter (4 espaces)' },
  { label: ':', text: ':', aria: 'Deux-points' },
  { label: '( )', text: '()', aria: 'Parenthèses' },
  { label: '==', text: ' == ', aria: 'Égal à' },
  { label: '!=', text: ' != ', aria: 'Différent de' },
  { label: '**', text: '**', aria: 'Puissance' },
  { label: '%', text: ' % ', aria: 'Reste de la division' },
  { label: 'range', text: 'range()', aria: 'range' },
  { label: 'print', text: 'print()', aria: 'print' },
];

/** insère `text` à la place de la sélection ; renvoie le texte et le curseur */
export function insertAt(
  code: string,
  start: number,
  end: number,
  text: string,
): { code: string; caret: number } {
  const next = code.slice(0, start) + text + code.slice(end);
  // dans « () » ou « range() » : le curseur entre les parenthèses
  const caret = start + (text.endsWith('()') ? text.length - 1 : text.length);
  return { code: next, caret };
}

/**
 * ⇥ sur une sélection de plusieurs lignes : 4 espaces au début de CHAQUE
 * ligne touchée ; sinon 4 espaces au curseur.
 */
export function indent(
  code: string,
  start: number,
  end: number,
): { code: string; selStart: number; selEnd: number } {
  const sel = code.slice(start, end);
  if (!sel.includes('\n')) {
    const r = insertAt(code, start, end, '    ');
    return { code: r.code, selStart: r.caret, selEnd: r.caret };
  }
  const lineStart = code.lastIndexOf('\n', start - 1) + 1;
  const block = code.slice(lineStart, end);
  const lines = block.split('\n');
  const out = lines.map((l) => `    ${l}`).join('\n');
  return {
    code: code.slice(0, lineStart) + out + code.slice(end),
    selStart: start + 4,
    selEnd: end + 4 * lines.length,
  };
}

/** message de l'interpréteur, complété quand il vient d'une limite du bac à sable */
export function explainError(message: string, code: string): string {
  if (message.includes('« input »'))
    return 'input n’existe pas ici : donne directement une valeur à la variable (x = 5).';
  if (/\bf["']/.test(code) && /attendu/.test(message))
    return `${message} Les f-strings ne sont pas prises en charge : écris print("x =", x).`;
  if (message.includes('pas de valeur décimale exacte') || message.includes('pas de valeur exacte'))
    return `${message} Ce Python calcule exactement : pas de racine non décimale (comme √2).`;
  if (message === 'Programme trop long.') return 'Programme trop long : 4 000 caractères au plus.';
  return message;
}

/** une valeur affichée sans déborder : 120 caractères au plus */
export const clip = (s: string, max = 120) => (s.length > max ? `${s.slice(0, max)}…` : s);
